import React, { useState } from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { FaFilePdf, FaSpinner, FaTimes } from "react-icons/fa";
import moment from "moment";

/**
 * PdfReportButton
 *
 * Props :
 *   motor    — objet moteur complet
 *   donnee   — { debut, fin, granularity }
 *   graphRef — ref React attachée au conteneur SelectionGraphique
 *
 * SelectionGraphique pose data-pdf-group sur chaque wrapper de groupe.
 */
export default function PdfReportButton({ motor, donnee, graphRef }) {
  const [loading, setLoading]     = useState(false);
  const [showModal, setShowModal] = useState(false);

  const [options, setOptions] = useState({
    comments:     true,
    vibState:     true,
    graphElec:    true,
    graphTemp:    true,
    graphRot:     true,
    graphRMS:     true,
    graphSpectre: true,
    graphWifi:    false,
  });

  const toggle = (key) => setOptions((prev) => ({ ...prev, [key]: !prev[key] }));

  const granularityLabel = { minute: "Minute", hour: "Heure", day: "Jour" };

  // ── Capture avec masquage temporaire des groupes décochés ─────────────────
  async function captureWithFilter(container) {
    const groupEls = Array.from(container.querySelectorAll("[data-pdf-group]"));
    const hidden = [];
    groupEls.forEach((el) => {
      const group = el.getAttribute("data-pdf-group");
      if (options[group] === false) {
        const prev = el.style.display;
        el.style.display = "none";
        hidden.push({ el, prev });
      }
    });

    const canvas = await html2canvas(container, {
      scale: 1.5,
      useCORS: true,
      backgroundColor: "#ffffff",
      logging: false,
      onclone: (doc) => { doc.body.style.background = "#ffffff"; },
    });

    hidden.forEach(({ el, prev }) => { el.style.display = prev; });
    return canvas;
  }

  /**
   * Calcule les points de coupe sûrs EN PIXELS CANVAS à partir des positions
   * relatives au conteneur capturé — indépendant du scroll et du zoom écran.
   *
   * On utilise offsetTop / offsetHeight en remontant jusqu'au conteneur,
   * ce qui donne la position réelle dans le flux du document (pas l'écran).
   */
  function getOffsetTop(el, container) {
    let top = 0;
    let cur = el;
    while (cur && cur !== container) {
      top += cur.offsetTop;
      cur = cur.offsetParent;
    }
    return top;
  }

  function getSafeCutsPx(container, canvasHeight, scale) {
    const cuts = new Set([0, canvasHeight]);

    // Hauteur totale du conteneur en pixels DOM
    const containerHeight = container.scrollHeight;
    const pxPerCanvasPx = canvasHeight / containerHeight;

    container.querySelectorAll(".recharts-wrapper").forEach((el) => {
      const top    = getOffsetTop(el, container);
      const bottom = top + el.offsetHeight;
      const topCanvas    = Math.round(top    * pxPerCanvasPx);
      const bottomCanvas = Math.round(bottom * pxPerCanvasPx);
      if (topCanvas > 0    && topCanvas < canvasHeight)    cuts.add(topCanvas);
      if (bottomCanvas > 0 && bottomCanvas < canvasHeight) cuts.add(bottomCanvas);
    });

    return Array.from(cuts).sort((a, b) => a - b);
  }

  // ── Génération PDF ─────────────────────────────────────────────────────────
  const handleGeneratePdf = async () => {
    if (!graphRef?.current) {
      alert("Impossible de capturer les graphiques. Veuillez d'abord lancer une recherche.");
      return;
    }

    setShowModal(false);
    setLoading(true);

    try {
      const pdf      = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const pageW    = pdf.internal.pageSize.getWidth();
      const pageH    = pdf.internal.pageSize.getHeight();
      const margin   = 14;
      const contentW = pageW - margin * 2;

      const bleu      = [30, 80, 160];
      const marron    = [120, 70, 40];
      const gris      = [100, 100, 100];
      const grisClair = [220, 220, 220];

      // ── PAGE 1 : Page de garde ────────────────────────────────────────────
      pdf.setFillColor(...bleu);
      pdf.rect(0, 0, pageW, 42, "F");

      pdf.setTextColor(255, 255, 255);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(22);
      pdf.text("Rapport de diagnostic", margin, 18);

      pdf.setFontSize(13);
      pdf.setFont("helvetica", "normal");
      pdf.text("Diagmotor — ALF Environnement", margin, 28);

      pdf.setFontSize(10);
      pdf.text(`Généré le ${moment().format("DD/MM/YYYY à HH:mm")}`, margin, 38);

      // Nom du moteur (gros, marron) — ligne 1
      pdf.setTextColor(...marron);
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(20);
      pdf.text(motor.nom_moteur || "Moteur sans nom", margin, 57);

      // Client sur la même ligne à droite, taille 15 bold marron
      if (motor.client) {
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(15);
        pdf.setTextColor(...marron);
        pdf.text(motor.client, pageW - margin, 57, { align: "right" });
      }

      pdf.setDrawColor(...grisClair);
      pdf.setLineWidth(0.4);
      pdf.line(margin, 62, pageW - margin, 62);

      let y    = 72;
      const col1 = margin;
      const col2 = pageW / 2 + 4;

      // Localisation sur toute la largeur (peut être longue)
      pdf.setFontSize(10);
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(...gris);
      pdf.text("Localisation", col1, y);
      pdf.setFont("helvetica", "normal");
      pdf.setTextColor(30, 30, 30);
      const locLines = pdf.splitTextToSize(motor.localisation || "—", contentW - 38);
      pdf.text(locLines, col1 + 38, y);
      y += locLines.length * 6 + 4;

      // Reste des infos en 2 colonnes
      const infoRows = [
        ["Type",            motor.type           || "—"],
        ["Alimentation",    motor.freqAlim  ? `${motor.freqAlim} Hz`                     : "—"],
        ["Fréq. rotation",  motor.freqRot   ? `${Math.round(motor.freqRot * 60)} tr/min` : "—"],
        ["Courant nominal", motor.courantNom? `${motor.courantNom} A`                    : "—"],
        ["Durée de fonct.", motor.dureeFct != null ? `${parseInt(motor.dureeFct)} h`     : "—"],
      ];

      // Placement 2 colonnes : paires sur col1, impaires sur col2
      pdf.setFontSize(10);
      infoRows.forEach(([label, value], i) => {
        const cx  = i % 2 === 0 ? col1 : col2;
        const cy  = y + Math.floor(i / 2) * 10;
        // Tronquer la valeur si elle dépasse la demi-largeur
        const maxW = col2 - col1 - 42;
        const truncatedValue = pdf.splitTextToSize(String(value), maxW)[0];
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(...gris);
        pdf.text(label, cx, cy);
        pdf.setFont("helvetica", "normal");
        pdf.setTextColor(30, 30, 30);
        pdf.text(truncatedValue, cx + 38, cy);
      });

      y += Math.ceil(infoRows.length / 2) * 10 + 10;

      pdf.setDrawColor(...grisClair);
      pdf.line(margin, y, pageW - margin, y);
      y += 10;

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(11);
      pdf.setTextColor(...bleu);
      pdf.text("Période analysée", margin, y);
      y += 8;

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(10);
      pdf.setTextColor(30, 30, 30);
      pdf.text(
        `Du ${moment(donnee.debut).format("DD/MM/YYYY")} au ${moment(donnee.fin).format("DD/MM/YYYY")}`,
        margin, y
      );
      y += 6;
      pdf.text(
        `Périodicité : ${granularityLabel[donnee.granularity] || donnee.granularity}`,
        margin, y
      );
      y += 14;

      if (options.comments && motor.commentaire) {
        pdf.setDrawColor(...grisClair);
        pdf.line(margin, y, pageW - margin, y);
        y += 10;
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(11);
        pdf.setTextColor(...bleu);
        pdf.text("Commentaires", margin, y);
        y += 7;
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(9);
        pdf.setTextColor(50, 50, 50);
        const lines = pdf.splitTextToSize(motor.commentaire, contentW);
        pdf.text(lines, margin, y);
        y += lines.length * 5 + 6;
      }

      if (options.vibState && motor.etatVibratoire?.length > 0) {
        pdf.setDrawColor(...grisClair);
        pdf.line(margin, y, pageW - margin, y);
        y += 10;
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(11);
        pdf.setTextColor(...bleu);
        pdf.text("État vibratoire au dernier relevé", margin, y);
        y += 7;
        motor.etatVibratoire.forEach((msg) => {
          const isRouge = msg.toLowerCase().includes("important");
          const isJaune = msg.toLowerCase().includes("surveiller") || msg.toLowerCase().includes("détectée");
          if (isRouge)      pdf.setTextColor(200, 30, 30);
          else if (isJaune) pdf.setTextColor(180, 130, 0);
          else              pdf.setTextColor(40, 140, 40);
          pdf.setFont("helvetica", "normal");
          pdf.setFontSize(9);
          const lines = pdf.splitTextToSize(`• ${msg}`, contentW - 4);
          pdf.text(lines, margin + 2, y);
          y += lines.length * 5 + 2;
        });
      }

      pdf.setTextColor(...gris);
      pdf.setFontSize(8);
      pdf.setFont("helvetica", "normal");
      pdf.text("ALF Environnement — Diagmotor — Document confidentiel", margin, pageH - 8);
      pdf.text("1", pageW - margin, pageH - 8, { align: "right" });

      // ── PAGE 2+ : Graphiques ──────────────────────────────────────────────
      pdf.addPage();
      let pageNum = 2;

      const addHeader = (first) => {
        pdf.setFillColor(...bleu);
        pdf.rect(0, 0, pageW, first ? 18 : 14, "F");
        pdf.setTextColor(255, 255, 255);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(first ? 12 : 10);
        pdf.text(
          first ? `Graphiques — ${motor.nom_moteur}` : `Graphiques (suite) — ${motor.nom_moteur}`,
          margin, first ? 12 : 10
        );
      };

      const addFooter = () => {
        pdf.setTextColor(...gris);
        pdf.setFontSize(8);
        pdf.setFont("helvetica", "normal");
        pdf.text("ALF Environnement — Diagmotor — Document confidentiel", margin, pageH - 8);
        pdf.text(String(pageNum), pageW - margin, pageH - 8, { align: "right" });
      };

      addHeader(true);

      // Capture (groupes décochés masqués temporairement)
      const canvas = await captureWithFilter(graphRef.current);

      const totalImgHmm  = (canvas.height * contentW) / canvas.width;
      const pxPerMm      = canvas.height / totalImgHmm;

      // Points de coupe sûrs calculés via offsetTop (indépendant du scroll/zoom)
      const sortedCuts = getSafeCutsPx(graphRef.current, canvas.height, 1.5);

      // ── Placement page par page ───────────────────────────────────────────
      const headerH     = 22;
      const headerHCont = 18;
      const footerH     = 12;

      let curMm   = 0;
      let isFirst = true;

      while (curMm < totalImgHmm - 0.5) {
        const usableH  = isFirst ? pageH - headerH - footerH : pageH - headerHCont - footerH;
        const topOff   = isFirst ? headerH : headerHCont;

        const srcTopPx   = Math.round(curMm * pxPerMm);
        const idealCutPx = Math.round((curMm + Math.min(usableH, totalImgHmm - curMm)) * pxPerMm);

        // Meilleur cut sûr ≤ idéal et > srcTop
        const bestCutPx = sortedCuts
          .filter((c) => c <= idealCutPx && c > srcTopPx)
          .pop() ?? idealCutPx;

        const srcHPx       = Math.max(bestCutPx - srcTopPx, 1);
        const realSliceHmm = srcHPx / pxPerMm;

        const sliceCanvas        = document.createElement("canvas");
        sliceCanvas.width        = canvas.width;
        sliceCanvas.height       = srcHPx;
        sliceCanvas.getContext("2d").drawImage(
          canvas, 0, srcTopPx, canvas.width, srcHPx, 0, 0, canvas.width, srcHPx
        );

        pdf.addImage(
          sliceCanvas.toDataURL("image/jpeg", 0.88),
          "JPEG", margin, topOff, contentW, realSliceHmm
        );
        addFooter();

        curMm  += realSliceHmm;
        isFirst = false;

        if (curMm < totalImgHmm - 0.5) {
          pdf.addPage();
          pageNum++;
          addHeader(false);
        }
      }

      const fileName = `rapport_${(motor.nom_moteur || "moteur").replace(/\s+/g, "_")}_${moment(donnee.debut).format("YYYYMMDD")}_${moment(donnee.fin).format("YYYYMMDD")}.pdf`;
      pdf.save(fileName);

    } catch (err) {
      console.error("Erreur génération PDF :", err);
      alert("Une erreur est survenue lors de la génération du rapport PDF.");
    } finally {
      setLoading(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        disabled={loading}
        title="Générer un rapport PDF des graphiques"
        className={`flex items-center gap-2 bg-white border-2 border-bleuSTB text-bleuSTB hover:bg-bleuSTB hover:text-white text-sm sm:text-base font-semibold px-4 py-2 rounded-lg transition-all duration-200 ${loading ? "opacity-60 cursor-not-allowed" : ""}`}
      >
        {loading ? (
          <><FaSpinner className="animate-spin" /><span>Génération...</span></>
        ) : (
          <><FaFilePdf className="text-lg" /><span>Rapport PDF</span></>
        )}
      </button> 

      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-semibold text-gray-800">Configurer le rapport PDF</h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors">
                <FaTimes className="text-xl" />
              </button>
            </div>

            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Informations générales</p>
            <OptionRow label="Fiche moteur (infos & période)" checked disabled icon="📋" />
            <OptionRow label="Commentaires"           checked={options.comments} onChange={() => toggle("comments")} icon="💬" />
            <OptionRow label="État vibratoire résumé" checked={options.vibState} onChange={() => toggle("vibState")} icon="📊" />

            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mt-4 mb-2">Graphiques</p>
            <OptionRow label="Courant & consommation"                  checked={options.graphElec}    onChange={() => toggle("graphElec")}    icon="⚡" />
            <OptionRow label="Température"                             checked={options.graphTemp}    onChange={() => toggle("graphTemp")}    icon="🌡️" />
            <OptionRow label="Vitesse de rotation & fréquence réseau"  checked={options.graphRot}     onChange={() => toggle("graphRot")}     icon="🔄" />
            <OptionRow label="Vitesse RMS vibratoire (axes X / Y / Z)" checked={options.graphRMS}     onChange={() => toggle("graphRMS")}     icon="〰️" />
            <OptionRow label="Amplitudes vibratoires (axes X / Y / Z)" checked={options.graphSpectre} onChange={() => toggle("graphSpectre")} icon="📈" />
            <OptionRow label="Signal Wi-Fi"                            checked={options.graphWifi}    onChange={() => toggle("graphWifi")}    icon="📶" />

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 px-4 py-2 rounded-lg border border-gray-300 text-gray-600 text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleGeneratePdf}
                className="flex-[2] flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-bleuSTB text-white text-sm font-semibold hover:opacity-90 transition-all"
              >
                <FaFilePdf /> Générer le PDF
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function OptionRow({ label, checked, onChange, disabled = false, icon }) {
  return (
    <label className={`flex items-center justify-between px-3 py-2 mb-1.5 rounded-lg border border-gray-200 bg-gray-50 transition-colors ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer hover:bg-gray-100"}`}>
      <span className="text-sm text-gray-700 flex items-center gap-2">
        <span>{icon}</span>
        {label}
        {disabled && <span className="text-xs text-gray-400 font-normal">(toujours inclus)</span>}
      </span>
      <input type="checkbox" checked={checked} onChange={onChange} disabled={disabled} className="w-4 h-4 accent-bleuSTB cursor-pointer" />
    </label>
  ); 
}